import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import {
  ICustomer,
  ICustomerBulkCreate,
  ICustomerCreate,
  ICustomerCreateUnregistered,
  ICustomerEntity,
  ICustomerLocation,
  ICustomerSummaryRequest,
  ICustomerSummaryResponse,
  ICustomerUpdate,
  ILocation,
  ILocationCustomerCreateLocation,
  IPaginatedResponse,
  LoadCustomerLocationsParams,
  LoadCustomerLocationsRequest,
  LoadCustomersRequest,
} from '@boma/supplier-portal/shared/entities';

@Injectable({
  providedIn: 'root',
})
export class CustomersService {
  _error_records: any[];
  _warning_records: [];
  _skipped_records: [];
  _valid_records: [];
  private base: string;
  private version: string;
  private data: any;
  private requiredImportFields = [
    'IndividualOrBusiness',
    'FirstName',
    'LastName',
    'Email',
    'Category/Definition',
    'DefaultLocationName',
    'RegisteredName',
    'TradingName',
    'RegistrationNumber',
    'TaxNumber',
    'ContactNumber',
    'PhysicalAddressLineOne',
    'PhysicalAddressLineTwo',
    'PhysicalAddressCity',
    'PostalAddressLineOne',
    'PostalAddressLineTwo',
    'PostalAddressCity',
    'PostalAddressPostalCode',
    'Country',
    'YearEndDate',
    'WeekStart',
    'WebsiteAddress',
    'Active',
    'Sector',
  ];

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this.base = configuration.apiRoute;
    this.version = configuration.apiVersion;
  }

  loadCustomers(params: LoadCustomersRequest) {
    const { entityId, page, perPage, query, customerType, searchTarget } = params;
    return this.httpClient.get<IPaginatedResponse<ICustomerEntity>>(
      `${this.base}api/v${this.version}/${entityId}/Customers`,
      {
        params: { page, perPage, query, customerType, searchTarget },
      }
    );
  }

  getCustomers(entityId: string) {
    return this.httpClient.get<ICustomerEntity>(
      `${this.base}api/v${this.version}/${entityId}/Customers`
    );
  }

  getCustomerById(entityId: string, customerId: string) {
    return this.httpClient.get<ICustomerEntity>(
      `${this.base}api/v${this.version}/${entityId}/Customers/${customerId}`,
      {
        params: { customerId },
      }
    );
  }

  getCustomerSalesSummary(entityId: string, params: ICustomerSummaryRequest) {
    return this.httpClient.post<ICustomerSummaryResponse>(
      `${this.base}api/v${this.version}/${entityId}/Customers/SalesSummary`,
      {
        ...params,
      }
    );
  }

  //query is a phone number make sure you use +27 (country prefix) and only used on external
  globalCustomerSearch(entityId: string, lookupType: string, query: string) {
    const bodyParams = {};
    bodyParams['lookupType'] = lookupType;

    //Todo this doesnt look right....
    let queryString = '';
    if (lookupType === 'External')
      queryString = '&query=' + encodeURIComponent(query);

    return this.httpClient.get<ICustomer[]>(
      `${this.base}api/v${this.version}/${entityId}/Customers/SalesCustomerLookup?lookupType=${lookupType}` +
        queryString
    );
  }

  createCustomer(entityId: string, customer: ICustomerCreate) {
    return this.httpClient.post<ICustomerEntity>(
      `${this.base}api/v${this.version}/${entityId}/Customers`,
      {
        ...customer,
      }
    );
  }

  createCustomerUnregistered(
    entityId: string,
    customer: ICustomerCreateUnregistered
  ) {
    return this.httpClient.post<ICustomerEntity>(
      `${this.base}api/v${this.version}/${entityId}/Customers/Unregistered`,
      {
        ...customer,
      }
    );
  }

  bulkUploadCustomers(entityId: string, customers: ICustomerBulkCreate[]) {
    return this.httpClient.post(
      `${this.base}api/v${this.version}/${entityId}/Customers/Bulk`,
      {
        customers: customers,
      }
    );
  }

  updateCustomer(
    entityId: string,
    customerId: string,
    customer: ICustomerUpdate
  ) {
    return this.httpClient.put<ICustomerEntity>(
      `${this.base}api/v${this.version}/${entityId}/Customers/${customerId}`,
      {
        ...customer,
      }
    );
  }

  // Link existing entity to user entity as a customer
  linkCustomer(
    entityId: string,
    customerEntityId: string,
    customer: ICustomerUpdate
  ) {
    return this.httpClient.post<ICustomer>(
      `${this.base}api/v${this.version}/${entityId}/Customers/${customerEntityId}`,
      {
        ...customer,
      }
    );
  }

  linkCustomerLocation(
    entityId: string,
    customerId: string,
    locationId: string
  ) {
    return this.httpClient.post<ICustomerLocation>(
      `${this.base}api/v${this.version}/${entityId}/Customers/${customerId}/${locationId}`,
      {}
    );
  }

  createCustomerLocation(
    entityId: string,
    customerId: string,
    location: ILocationCustomerCreateLocation | ILocation
  ) {
    return this.httpClient.post<ICustomerEntity>(
      `${this.base}api/v${this.version}/${entityId}/Customers/Locations/${customerId}`,
      {
        ...location,
      }
    );
  }

  updateCustomerLocation(
    entityId: string,
    customerId: string,
    locationId: string,
    location: ILocation
  ) {
    return this.httpClient.put<ICustomerEntity>(
      `${this.base}api/v${this.version}/${entityId}/Customers/Locations/${customerId}/${locationId}`,
      {
        ...location,
      }
    );
  }

  loadCustomersLocations(
    params: LoadCustomerLocationsParams,
    body: LoadCustomerLocationsRequest
  ) {
    const { entityId, page, perPage } = params;
    const queryParams = [];
    let urlQuery = '';
    if (page) {
      queryParams.push(`page=${page}`);
    }

    if (perPage) {
      queryParams.push(`perPage=${perPage}`);
    }

    if (queryParams.length > 0) {
      urlQuery = `?${queryParams.join('&')}`;
    }

    return this.httpClient.post<IPaginatedResponse<ICustomerLocation>>(
      `${this.base}api/v${this.version}/${entityId}/Customers/Locations/list${urlQuery}`,
      body
    );
  }

  getCustomerLocationById(
    entityId: string,
    customerId: string,
    locationId: string
  ) {
    return this.httpClient.get<ILocation>(
      `${this.base}api/v${this.version}/${entityId}/Customers/Locations/${customerId}/${locationId}`
    );
  }
}
